import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowSquareOut, Storefront, Tag, CircleNotch, BellRinging, Bell, Check } from '@phosphor-icons/react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useCurrency } from '@/context/CurrencyContext';
import { trackAffiliateClick } from '@/lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const flagEmoji = { AU: '\uD83C\uDDE6\uD83C\uDDFA', UK: '\uD83C\uDDEC\uD83C\uDDE7', US: '\uD83C\uDDFA\uD83C\uDDF8' };

export default function ShopPickModal({ shoe, onClose }) {
  const { format } = useCurrency();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loadingId, setLoadingId] = useState(null);
  const [watch, setWatch] = useState(null);           // existing watch doc, null = none
  const [watchLoading, setWatchLoading] = useState(false);
  const [watchError, setWatchError] = useState('');

  // Top 3 cheapest in-stock retailers, sorted by price
  const picks = (shoe?.store_prices || [])
    .filter(p => p.in_stock !== false)
    .sort((a, b) => (a.price || Infinity) - (b.price || Infinity))
    .slice(0, 3);
  const cheapest = picks[0];

  // Target price: default to 10% below cheapest, rounded down to whole
  const defaultTarget = cheapest ? Math.max(1, Math.floor(cheapest.price * 0.9)) : 0;
  const [targetPrice, setTargetPrice] = useState(defaultTarget);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  useEffect(() => { setTargetPrice(defaultTarget); }, [defaultTarget]);

  // Check for existing watch when modal opens (logged-in users only)
  useEffect(() => {
    if (!user || !cheapest || !shoe?.shoe_id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/price-watches`, {
          params: { shoe_id: shoe.shoe_id, store_id: cheapest.store_id },
          withCredentials: true,
        });
        if (!cancelled && Array.isArray(data) && data.length > 0) {
          setWatch(data[0]);
          if (data[0].target_price) setTargetPrice(Math.round(data[0].target_price));
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [user, shoe?.shoe_id, cheapest?.store_id]);

  if (!shoe) return null;

  const goBuy = async (e, p) => {
    e.preventDefault();
    setLoadingId(p.store_id);
    // GA: most-important event for revenue optimisation
    trackAffiliateClick({
      retailerId: p.store_id,
      network: p.affiliate_network,
      region: p.region,
      shoeId: shoe.shoe_id,
      brand: shoe.brand,
      price: p.price,
    });
    try {
      const { data } = await axios.post(`${API}/affiliate/click`,
        { shoe_id: shoe.shoe_id, store_id: p.store_id },
        { withCredentials: true }
      );
      window.open(data.affiliate_url || p.product_url || '#', '_blank', 'noopener,noreferrer');
    } catch {
      window.open(p.product_url || '#', '_blank', 'noopener,noreferrer');
    } finally {
      setLoadingId(null);
    }
  };

  const savings = picks.length > 1 && cheapest
    ? Math.max(...picks.map(p => p.price || 0)) - (cheapest.price || 0)
    : 0;

  const saveWatch = async () => {
    setWatchError('');
    if (!user) {
      onClose();
      navigate('/login?redirect=/quiz');
      return;
    }
    if (!cheapest) return;
    setWatchLoading(true);
    try {
      const { data } = await axios.post(`${API}/price-watches`,
        {
          shoe_id: shoe.shoe_id,
          store_id: cheapest.store_id,
          target_price: targetPrice || null,
        },
        { withCredentials: true }
      );
      setWatch(data);
    } catch (err) {
      setWatchError(err?.response?.data?.detail || 'Could not create alert.');
    } finally {
      setWatchLoading(false);
    }
  };

  const removeWatch = async () => {
    if (!watch?.watch_id) return;
    setWatchLoading(true);
    try {
      await axios.delete(`${API}/price-watches/${watch.watch_id}`, { withCredentials: true });
      setWatch(null);
    } catch (err) {
      setWatchError(err?.response?.data?.detail || 'Could not remove alert.');
    } finally {
      setWatchLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="shop-pick-modal-backdrop"
      >
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.96 }}
          transition={{ type: 'spring', stiffness: 340, damping: 28 }}
          className="bg-white w-full max-w-[560px] border-2 border-[#0A0A0A] shadow-2xl relative"
          onClick={(e) => e.stopPropagation()}
          data-testid="shop-pick-modal"
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-[#52525B] hover:text-[#FF3B30] transition-colors"
            data-testid="shop-pick-close-btn"
          >
            <X size={18} weight="bold" />
          </button>

          {/* Header */}
          <div className="p-5 pr-12 border-b border-[#F4F4F5] flex items-center gap-4">
            <img
              src={shoe.image_url}
              alt={`${shoe.brand} ${shoe.model}`}
              className="w-16 h-16 object-cover flex-shrink-0 bg-[#FAFAFA]"
            />
            <div className="min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#52525B]">{shoe.brand}</p>
              <h3 className="font-heading font-bold text-base mt-0.5 truncate">{shoe.model}</h3>
              <p className="text-[11px] text-[#A1A1AA] uppercase tracking-wide font-bold mt-0.5 inline-flex items-center gap-1">
                <Tag size={10} weight="bold" /> Top {picks.length} retailer{picks.length !== 1 ? 's' : ''}
                {savings > 0 && (
                  <span className="ml-2 text-[#10B981]">Save {format(savings, cheapest.currency)}</span>
                )}
              </p>
            </div>
          </div>

          {/* Picks */}
          <div className="p-5 space-y-2" data-testid="shop-pick-list">
            {picks.length === 0 && (
              <div className="text-center py-8 text-sm text-[#52525B]" data-testid="shop-pick-empty">
                <Storefront size={28} className="mx-auto mb-2 text-[#A1A1AA]" />
                No retailers currently in stock for this shoe.
              </div>
            )}
            {picks.map((p, idx) => {
              const isCheapest = idx === 0;
              const isLoading = loadingId === p.store_id;
              return (
                <button
                  key={`${p.store_id}-${idx}`}
                  type="button"
                  onClick={(e) => goBuy(e, p)}
                  disabled={isLoading}
                  className={`w-full flex items-center justify-between gap-3 p-3 border-2 transition-all text-left group/pick
                    ${isCheapest ? 'border-[#FF3B30] bg-[#FFF5F4]' : 'border-[#E4E4E7] hover:border-[#0A0A0A]'}
                    disabled:opacity-60 disabled:cursor-wait`}
                  data-testid={`shop-pick-row-${p.store_id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xl leading-none flex-shrink-0" aria-label={p.store_region}>
                      {flagEmoji[p.store_region] || '\uD83C\uDF0D'}
                    </span>
                    <div className="min-w-0">
                      <p className="font-bold text-sm text-[#0A0A0A] truncate">{p.store_name}</p>
                      <p className="text-[11px] text-[#52525B] uppercase tracking-wide font-medium flex items-center gap-1.5">
                        {p.store_region}
                        {isCheapest && (
                          <span className="bg-[#FF3B30] text-white px-1.5 py-0.5 text-[9px] tracking-[0.1em] font-bold">BEST PRICE</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`font-bold text-lg ${isCheapest ? 'text-[#FF3B30]' : 'text-[#0A0A0A]'}`} data-testid={`shop-pick-price-${p.store_id}`}>
                      {format(p.price, p.currency)}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold uppercase tracking-wide transition-colors
                        ${isCheapest ? 'bg-[#FF3B30] text-white group-hover/pick:bg-[#D62828]' : 'bg-[#0A0A0A] text-white group-hover/pick:bg-[#FF3B30]'}`}
                    >
                      {isLoading ? (<><CircleNotch size={12} className="animate-spin" /> Loading</>) : (<>Shop <ArrowSquareOut size={12} weight="bold" /></>)}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Drop alert toggle */}
          {cheapest && (
            <div className="px-5 pb-4 border-t border-[#F4F4F5] pt-4" data-testid="drop-alert-section">
              {watch ? (
                <div className="flex items-center justify-between gap-3 border-2 border-[#10B981] bg-[#F0FDF4] p-3">
                  <div className="flex items-start gap-2 min-w-0">
                    <BellRinging size={18} weight="duotone" className="text-[#10B981] flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-[#0A0A0A]">Alert active</p>
                      <p className="text-[11px] text-[#52525B] mt-0.5">
                        We'll ping you when <strong>{watch.store_name}</strong>
                        {watch.target_price
                          ? <> drops below <strong>{format(watch.target_price, watch.currency || cheapest.currency)}</strong>.</>
                          : <> drops by 5% or more.</>
                        }
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={removeWatch}
                    disabled={watchLoading}
                    className="text-[11px] font-bold uppercase tracking-wide text-[#52525B] hover:text-[#FF3B30] transition-colors flex-shrink-0 disabled:opacity-60"
                    data-testid="drop-alert-remove-btn"
                  >
                    {watchLoading ? '…' : 'Remove'}
                  </button>
                </div>
              ) : (
                <div className="border-2 border-[#E4E4E7] p-3">
                  <div className="flex items-start gap-2 mb-2.5">
                    <Bell size={16} weight="duotone" className="text-[#FF3B30] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wide text-[#0A0A0A]">Price drop alert</p>
                      <p className="text-[11px] text-[#52525B] mt-0.5">
                        Get a real-time ping when <strong>{cheapest.store_name}</strong> drops the price.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center flex-1 border-2 border-[#E4E4E7] focus-within:border-[#0A0A0A]">
                      <span className="px-2 text-[11px] font-bold uppercase tracking-wide text-[#52525B] border-r border-[#E4E4E7]">Below</span>
                      <input
                        type="number"
                        min="1"
                        value={targetPrice || ''}
                        onChange={(e) => setTargetPrice(e.target.value === '' ? '' : Number(e.target.value))}
                        placeholder="Any drop"
                        className="w-full px-2 py-2 text-sm font-bold text-[#0A0A0A] outline-none bg-transparent"
                        data-testid="drop-alert-target-input"
                        aria-label="Target price"
                      />
                      <span className="px-2 text-[11px] font-medium text-[#A1A1AA]">{cheapest.currency}</span>
                    </div>
                    <button
                      type="button"
                      onClick={saveWatch}
                      disabled={watchLoading}
                      className="inline-flex items-center gap-1.5 bg-[#0A0A0A] text-white text-[11px] font-bold uppercase tracking-wide px-4 py-2.5 hover:bg-[#FF3B30] transition-colors disabled:opacity-60"
                      data-testid="drop-alert-notify-btn"
                    >
                      {watchLoading ? <CircleNotch size={12} className="animate-spin" /> : <Check size={12} weight="bold" />}
                      Notify me
                    </button>
                  </div>
                  {!user && (
                    <p className="text-[10px] text-[#A1A1AA] mt-2" data-testid="drop-alert-signin-hint">
                      You'll be asked to sign in so we can send alerts to your inbox.
                    </p>
                  )}
                  {watchError && (
                    <p className="text-[10px] text-[#FF3B30] mt-2 font-bold" data-testid="drop-alert-error">{watchError}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Disclosure */}
          <div className="px-5 pb-4 border-t border-[#F4F4F5] pt-3">
            <p className="text-[10px] text-[#A1A1AA] leading-relaxed">
              We may earn a small commission on qualifying purchases — at no cost to you. Prices may change on the retailer's site.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
